import { z } from "zod";
import { AnswerConfidence, AttemptMediaKind, AttemptStatus, DeviceRole, Difficulty, ExamStatus, ExamTimingMode, ProctorEventType, ProctorSeverity, QuestionSource, QuestionStatus, QuestionType, RecordingSource, UserRole, } from "./enums.js";
/**
 * Entity schemas mirror the D1 tables in apps/api/migrations, with one
 * deliberate difference: `*_json` TEXT columns are exposed to clients as parsed
 * values. The API is responsible for JSON.parse / JSON.stringify at the edge.
 *
 * Convention: ids are text uuid, timestamps are epoch milliseconds (integer).
 */
export const Id = z.string().uuid();
export const EpochMs = z.number().int().nonnegative();
/* ------------------------------------------------------------------ */
/* users                                                               */
/* ------------------------------------------------------------------ */
/** Never contains password_hash — that column must not leave the Worker. */
export const User = z.object({
    id: Id,
    email: z.string().email(),
    role: UserRole,
    name: z.string().min(1),
});
export const LoginInput = z.object({
    email: z.string().email(),
    password: z.string().min(8).max(200),
});
/* ------------------------------------------------------------------ */
/* subjects / topics                                                   */
/* ------------------------------------------------------------------ */
export const Subject = z.object({
    id: Id,
    name: z.string().min(1),
    slug: z
        .string()
        .min(1)
        .regex(/^[a-z0-9-]+$/, "slug must be lowercase kebab-case"),
});
export const SubjectInput = Subject.omit({ id: true });
/**
 * The topic tree. A **subtopic is a topic with a `parent_id`**, so the content
 * pack's two-level `topic_path` survives import intact instead of being
 * truncated to its head.
 */
/**
 * A link someone pasted, constrained to what a browser can safely open.
 *
 * The scheme allowlist is the point: without it `javascript:` is a valid URL
 * and this field ends up rendered into an anchor on a page the student is
 * looking at. Blank is normalised to null so an emptied input clears the link
 * rather than storing "".
 */
export const HttpUrl = z
    .string()
    .trim()
    // Matched rather than parsed: this package has no DOM lib, so `URL` is not
    // available here, and an anchored scheme test is what the check is actually
    // about anyway.
    .regex(/^https?:\/\/\S+$/i, "Must be a http(s) link");
export const Topic = z.object({
    id: Id,
    subject_id: Id,
    name: z.string().min(1),
    week_no: z.number().int().min(1).max(52).nullable(),
    parent_id: Id.nullable(),
    /**
     * A lesson for this whole topic, shown on the result page beside any wrong
     * question in it that has no link of its own. One video per topic covers a
     * paper without having to touch every question.
     */
    video_url: HttpUrl.nullable(),
});
export const TopicInput = Topic.omit({ id: true });
/* ------------------------------------------------------------------ */
/* questions                                                           */
/* ------------------------------------------------------------------ */
/**
 * One selectable option of an MCQ/MSQ. The canonical shape — the DB column, the
 * importer and the UI all use exactly this. `key` (upper-case "A", "B", …) is
 * what a `CorrectAnswer` and a student's response reference, never the index,
 * so options can be reordered without invalidating an answer key.
 */
export const QuestionOption = z.object({
    key: z.string().min(1),
    text: z.string(),
});
/**
 * Option keys assigned by position on import: A, B, C, D, …
 *
 * Real MSQs routinely run to five and six options, so nothing anywhere may assume
 * four. There is no cap in the DB, in `QuestionOption`, or in scoring (which
 * compares keys, never indices) — this list is only the letter supply used
 * when a content pack gives option *text* in order.
 */
export const OPTION_KEYS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
/**
 * Correct answer shape depends on `type`:
 *  - MCQ -> { type: "MCQ", key: "a" }
 *  - MSQ -> { type: "MSQ", keys: ["a","c"] }
 *  - NAT -> { type: "NAT", value: 3.14, tolerance: 0.01 }
 */
/**
 * One check on a CODE answer.
 *
 * `call` is an expression evaluated after the student's source, and `expect`
 * is the value it must equal — compared by JSON shape, so arrays and objects
 * work without the author writing a comparator.
 *
 * Deliberately not "arbitrary assertion code": a free-form assertion is a
 * second program to debug, written by whoever is authoring a question at
 * midnight, and when it is wrong the STUDENT is the one who fails.
 */
export const CodeTest = z.object({
    /** Shown to the student, so it has to read as a requirement. */
    name: z.string().min(1).max(120),
    /** e.g. `countTo(5)`. Evaluated after the submitted source. */
    call: z.string().min(1).max(500),
    /** Compared by JSON equality against what `call` returns. */
    expect: z.unknown(),
});
/** One test's outcome, as reported by the browser that ran it. */
export const CodeTestResult = z.object({
    name: z.string(),
    passed: z.boolean(),
    /** The failure, trimmed. Null when it passed. */
    detail: z.string().max(500).nullable().default(null),
});
export const CorrectAnswer = z.discriminatedUnion("type", [
    z.object({ type: z.literal("MCQ"), key: z.string().min(1) }),
    z.object({ type: z.literal("MSQ"), keys: z.array(z.string().min(1)).min(1) }),
    z.object({
        type: z.literal("NAT"),
        value: z.number(),
        tolerance: z.number().nonnegative().default(0),
    }),
    /**
     * A CODE question has no key — the tests are the key, and they live on the
     * question. This variant exists so the column stays non-null and every
     * existing `switch` over the union still has to handle the new type rather
     * than silently falling through.
     */
    z.object({ type: z.literal("CODE") }),
]);
/** What a student sends back for a question. Mirrors CorrectAnswer minus grading data. */
export const AnswerResponse = z.discriminatedUnion("type", [
    z.object({ type: z.literal("MCQ"), key: z.string().min(1).nullable() }),
    z.object({ type: z.literal("MSQ"), keys: z.array(z.string().min(1)) }),
    z.object({ type: z.literal("NAT"), value: z.number().nullable() }),
    /**
     * The submitted program, plus what the student's browser reported when it
     * ran the tests.
     *
     * The counts are stored because they are what the mark is computed from, and
     * the SOURCE is stored because the counts cannot be trusted on their own —
     * they are produced on the student's machine. Anyone can re-run the source
     * against the question's tests later; nobody can reconstruct it from a pair
     * of numbers.
     */
    z.object({
        type: z.literal("CODE"),
        source: z.string().max(20_000),
        passed: z.number().int().nonnegative(),
        total: z.number().int().nonnegative(),
        results: z.array(CodeTestResult).max(50).default([]),
    }),
]);
export const Question = z.object({
    id: Id,
    topic_id: Id,
    /** The content pack's own id ("maths-1-gen-018"); unique, so re-import updates. */
    external_id: z.string().nullable(),
    type: QuestionType,
    body_md: z.string().min(1),
    options: z.array(QuestionOption),
    correct_answer: CorrectAnswer,
    solution_md: z.string().nullable(),
    /**
     * Where to send a student who got THIS question wrong. Shown on their result
     * page, next to the question, and nowhere else — during the exam it would be
     * an answer key with extra steps.
     *
     * A video and a page are not substitutes: a video is better for a first
     * explanation, a page for looking something up mid-exercise. Both optional.
     */
    video_url: HttpUrl.nullable(),
    reading_url: HttpUrl.nullable(),
    /** CODE only: what the editor opens with. Null for every other type. */
    starter_code: z.string().max(20_000).nullable(),
    /** CODE only: the checks. Null for every other type. */
    tests: z.array(CodeTest).nullable(),
    difficulty: Difficulty,
    source: QuestionSource,
    status: QuestionStatus,
    /**
     * Marks the author suggests. The authoritative per-paper value is
     * `exam_questions.marks` — the official mark scheme is unpublished, so marks
     * and negative marking stay configurable per exam.
     */
    marks: z.number().positive().max(100),
    confidence: AnswerConfidence.nullable(),
    source_url: z.string().nullable(),
    /**
     * A human has checked this answer key against the question. Upstream keys are
     * **not** trustworthy — the sources we import from truncate numeric answers
     * instead of rounding (a true 3.567 is published as 3.56), so an imported NAT
     * key is unverified until someone confirms it. A NAT question with
     * `key_verified: false` cannot be attached to an exam.
     */
    key_verified: z.boolean(),
    /** Where the row came from, e.g. "content-pack:maths-1" or "claude-generate". */
    import_source: z.string().nullable(),
    /**
     * The question refers to a figure. Many upstream questions point at images
     * whose hosts no longer resolve, so a question with `requires_figure: true`
     * and no `figure_r2_key` is unanswerable and is kept out of every paper.
     */
    requires_figure: z.boolean(),
    /** R2 key of the stored figure; null means the figure is missing. */
    figure_r2_key: z.string().nullable(),
    created_at: EpochMs,
});
/**
 * Question as served to a student mid-exam. The answer key, the solution and
 * the authoring metadata are all stripped — the API builds this from a SELECT
 * that never reads those columns.
 */
export const StudentQuestion = Question.omit({
    correct_answer: true,
    solution_md: true,
    // Omitted for the same reason as the solution: a link to the explanation of
    // this exact question, handed over mid-exam, is an answer key with extra
    // steps. The result page is where these belong.
    video_url: true,
    reading_url: true,
    // The tests ARE the answer key for a CODE question. A student who can read
    // `expect` can hard-code it; the visible ones are re-sent, stripped, by the
    // paper endpoint.
    tests: true,
    status: true,
    confidence: true,
    source_url: true,
    external_id: true,
    key_verified: true,
    import_source: true,
    requires_figure: true,
    figure_r2_key: true,
}).extend({
    /**
     * Signed URL for the question's figure, or null when it has none. The student
     * never receives the raw R2 key.
     */
    figure_url: z.string().nullable(),
    /**
     * The question's own subject (via topic → subject). A multi-subject exam mixes
     * subjects, so the runner tabs/groups the paper by these, not by the exam's
     * primary subject.
     */
    subject_id: Id,
    subject_name: z.string(),
});
export const QuestionInput = Question.omit({ id: true, created_at: true });
/* ------------------------------------------------------------------ */
/* exams                                                               */
/* ------------------------------------------------------------------ */
export const Exam = z.object({
    id: Id,
    /**
     * The exam's primary/display subject. An exam may hold questions from OTHER
     * subjects too (a real Qualifier is English I + Maths I + Stats I + CT in one
     * sitting) — group the runner by each question's own subject, not this. Kept
     * non-null so single-subject exams are unchanged.
     */
    subject_id: Id,
    title: z.string().min(1),
    /**
     * The clock model. `from_start` (default) times the duration from the
     * attempt's own start; `scheduled` anchors the window to `scheduled_at`.
     */
    timing_mode: ExamTimingMode,
    /** Fixed sitting time — only meaningful in `scheduled` mode. */
    scheduled_at: EpochMs.nullable(),
    /**
     * Optional window in which the student may START. Both null means startable
     * anytime; the duration countdown is independent of these bounds.
     */
    available_from: EpochMs.nullable(),
    available_until: EpochMs.nullable(),
    duration_min: z.number().int().positive(),
    /**
     * Minutes from the attempt's start before a manual submit is allowed. 0 (the
     * default) means submit anytime. Server-authoritative — the submit endpoint
     * rejects an early press. Auto-submit at the duration end is never blocked.
     */
    min_submit_after_min: z.number().int().nonnegative(),
    /**
     * After submit, may the student see the answer key + solutions + per-question
     * correctness on their result page? Default true (practice exams reveal
     * everything). An invigilated exam can set false to withhold the key while
     * still showing the performance breakdown (scores/accuracy).
     */
    show_answers_after_submit: z.boolean(),
    /**
     * May the student see which topics this exam covers, before sitting it?
     *
     * Default false. Publishing the topics is what makes "revise for the exam"
     * mean something for a test of taught material; for a diagnostic it changes
     * the answer being measured, so it is a per-exam decision. The list itself
     * is derived from the questions on the paper and never stored, so it cannot
     * drift when the paper is edited.
     */
    syllabus_visible: z.boolean(),
    /**
     * After submitting, ask the student to photograph their rough sheets.
     *
     * Default false. It was written for a maths qualifier, where the working on
     * the sheets is the evidence. A multiple-choice paper produces no sheets and
     * a coding question produces none either — the work is already on the
     * server — so the step asks the student to photograph nothing, at the exact
     * moment the only instruction that matters is "keep this page open until
     * the recording finishes uploading".
     */
    collect_answer_sheets: z.boolean(),
    /**
     * Require a paired phone as the side camera.
     *
     * Default false. Where these exams are sat the connection is poor and the
     * phone is in the room anyway, so a third video stream competes with the two
     * recordings that matter more — on a slow link, insisting on the phone can
     * cost you the screen recording. It also decides whether missing phone
     * footage is a review flag: a camera nobody was asked to set up is not a
     * missing camera.
     */
    require_phone: z.boolean(),
    /**
     * Give the student an on-screen scratchpad for rough work.
     *
     * Default TRUE — with the answer-sheet step gone there is nowhere else to
     * work something out. Nothing is stored server-side: the screen recording
     * already captures the working in context, so uploading it again would be a
     * second copy paid for on the connection this app tries hardest to protect.
     */
    allow_scratchpad: z.boolean(),
    covers_weeks: z.array(z.number().int().min(1).max(52)),
    status: ExamStatus,
    created_at: EpochMs,
});
export const ExamInput = Exam.omit({ id: true, created_at: true });
export const ExamQuestion = z.object({
    id: Id,
    exam_id: Id,
    question_id: Id,
    order_idx: z.number().int().nonnegative(),
    marks: z.number(),
    negative_marks: z.number(),
});
export const ExamQuestionInput = ExamQuestion.omit({ id: true });
/* ------------------------------------------------------------------ */
/* attempts / answers                                                  */
/* ------------------------------------------------------------------ */
export const Attempt = z.object({
    id: Id,
    exam_id: Id,
    user_id: Id,
    started_at: EpochMs.nullable(),
    submitted_at: EpochMs.nullable(),
    score: z.number().nullable(),
    max_score: z.number().nullable(),
    status: AttemptStatus,
    /**
     * 1 for a first sitting, 2 for the first retake, and so on. A retake is a
     * NEW attempt against the same paper — the earlier one keeps its answers,
     * recordings and events and stays reviewable.
     */
    attempt_no: z.number().int().min(1),
    /**
     * Minutes granted (or, if negative, taken away) on top of the exam's
     * duration, for THIS attempt only. Held here rather than on the exam so
     * that accommodating one student never changes anyone else's clock.
     */
    extra_time_min: z.number().int(),
    /** How many times the laptop went silent and came back mid-attempt. */
    rejoin_count: z.number().int().nonnegative(),
    /** Total time the laptop was unreachable while the attempt was running. */
    offline_ms: z.number().int().nonnegative(),
});
/**
 * Offsets, in ms from `attempts.started_at`, at which the student opened a
 * question. One entry per open. This is what lets the admin review screen jump
 * the recording to the moment the student was looking at a given question, so it is
 * recorded rather than estimated from cumulative `time_spent_ms`.
 */
export const VisitOffsets = z.array(z.number().int().nonnegative()).max(500);
export const Answer = z.object({
    id: Id,
    attempt_id: Id,
    question_id: Id,
    response: AnswerResponse.nullable(),
    is_correct: z.boolean().nullable(),
    marks_awarded: z.number().nullable(),
    time_spent_ms: z.number().int().nonnegative(),
    visit_count: z.number().int().nonnegative(),
    visit_offsets_ms: VisitOffsets,
    /**
     * The student's "mark for review" flag. Persisted rather than kept in
     * sessionStorage so a browser crash or a laptop swap mid-exam cannot silently
     * lose it.
     */
    marked_for_review: z.boolean(),
});
/** Payload the exam UI sends when saving one answer. */
export const SaveAnswerInput = z.object({
    question_id: Id,
    response: AnswerResponse.nullable(),
    time_spent_ms: z.number().int().nonnegative().default(0),
    /**
     * New visit offsets since the last save, in ms from `attempts.started_at`.
     * Appended server-side; duplicates within 250 ms of an existing offset are
     * dropped so a retried request cannot inflate the timeline.
     */
    visit_offsets_ms: VisitOffsets.default([]),
    /** Omit to leave the stored flag untouched. */
    marked_for_review: z.boolean().optional(),
});
/**
 * Standalone visit ping — the exam client sends this when the student opens a
 * question without changing the answer, so the review timeline stays complete.
 */
export const RecordVisitInput = z.object({
    question_id: Id,
    offset_ms: z.number().int().nonnegative(),
    /** Time spent on the previously open question, in ms. */
    time_spent_ms: z.number().int().nonnegative().default(0),
    previous_question_id: Id.nullable().default(null),
});
/* ------------------------------------------------------------------ */
/* proctoring                                                          */
/* ------------------------------------------------------------------ */
export const ProctorEvent = z.object({
    id: Id,
    attempt_id: Id,
    ts_ms: EpochMs,
    type: ProctorEventType,
    severity: ProctorSeverity,
    metadata: z.record(z.unknown()).nullable(),
});
export const ProctorEventInput = z.object({
    ts_ms: EpochMs,
    type: ProctorEventType,
    severity: ProctorSeverity.optional(),
    metadata: z.record(z.unknown()).nullable().optional(),
});
/** Clients batch events and flush periodically. */
export const ProctorEventBatchInput = z.object({
    events: z.array(ProctorEventInput).min(1).max(100),
});
/* ------------------------------------------------------------------ */
/* media                                                               */
/* ------------------------------------------------------------------ */
export const Recording = z.object({
    id: Id,
    attempt_id: Id,
    source: RecordingSource,
    chunk_index: z.number().int().nonnegative(),
    /**
     * Which MediaRecorder session produced this chunk.
     *
     * Only the first blob of a session carries the WebM header, so chunks from
     * two sessions cannot be concatenated and played as one — even when they are
     * adjacent in time, which they are whenever the recorder restarts itself
     * after a stall, or when recording carries on from setup into the exam.
     */
    session_idx: z.number().int().nonnegative().default(0),
    r2_key: z.string().min(1),
    started_ms: EpochMs,
    duration_ms: z.number().int().nonnegative(),
    bytes: z.number().int().nonnegative(),
});
export const AttemptMedia = z.object({
    id: Id,
    attempt_id: Id,
    kind: AttemptMediaKind,
    r2_key: z.string().min(1),
    created_at: EpochMs,
});
/* ------------------------------------------------------------------ */
/* device pairing                                                      */
/* ------------------------------------------------------------------ */
export const DeviceSession = z.object({
    id: Id,
    attempt_id: Id,
    role: DeviceRole,
    pair_code: z.string().min(4).max(12),
    connected_at: EpochMs.nullable(),
    last_heartbeat_at: EpochMs.nullable(),
});
export const PairCodeInput = z.object({
    pair_code: z.string().min(4).max(12),
});
//# sourceMappingURL=schemas.js.map